import { Mail, Phone } from "lucide-react";
import { useEffect, useRef } from "react";

import { CallLink } from "@/components/CallLink";
import { daysSince, euros, frDate, initials, isDormant, isOverdue, STATUS_LABELS } from "@/components/pipeline/utils";
import { dur, gsap, hoverLift } from "@/themes/rapport/motion";

const tint = (s) => [...(s || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % 4;
const activity = (r) => (daysSince(r.updated_at) ? `il y a ${daysSince(r.updated_at)} j` : "aujourd'hui");

const Card = ({ r, checked, onToggle, onOpen, dormantDays }) => (
  <article className={`cx-card${checked ? " is-selected" : ""}`} data-lift="" data-lead-id={r.lead_id} data-testid={`pipeline-card-${r.lead_id}`}>
    <button type="button" className="cx-card__link" onClick={() => onOpen(r.lead_id)} aria-label={`Ouvrir ${r.title}`} data-testid={`pipeline-row-open-${r.lead_id}`} />
    <header className="cx-card__top">
      <span className={`av av--lg av--t${tint(r.title)}`} aria-hidden="true">{initials(r.title)}</span>
      <input type="checkbox" className="check ldp-check" checked={checked} onChange={() => onToggle(r.lead_id)} aria-label="Sélectionner" data-testid={`table-row-checkbox-${r.lead_id}`} />
    </header>
    <h3 className="cx-card__name">{r.title}</h3>
    <p className="cx-card__role">{r.pipeline} · {r.step}</p>
    <dl className="cx-card__facts">
      <div><dt>Montant</dt><dd>{euros(r.amount) || "—"}</dd></div>
      <div><dt>Dernière activité</dt><dd className={isDormant(r, dormantDays) ? "ldp-warn" : ""}>{activity(r)}</dd></div>
    </dl>
    <div className="cx-card__health ldp-card__state">
      <span className={`pl-status s-${r.status}`}>{STATUS_LABELS[r.status] || r.status}</span>
      {r.remind_date && <span className={`pl-badge ${isOverdue(r) ? "danger" : "info"}`}>Relance {frDate(r.remind_date)}</span>}
    </div>
    <footer className="cx-card__foot">
      <span className="chip ldp-owner" title={r.owner_name || "Non attribuée"}><span className="av av--xs av--t1">{initials(r.owner_name)}</span>{r.owner_name || "Non attribuée"}</span>
      <span className="cx-card__acts">
        {r.emails?.[0] ? <a className="ibtn ibtn--sm" href={`mailto:${r.emails[0]}`} aria-label={`Écrire à ${r.emails[0]}`} title={r.emails[0]} data-testid={`card-mail-${r.lead_id}`}><Mail size={14} /></a>
          : <button type="button" className="ibtn ibtn--sm" disabled title="Aucune adresse e-mail connue" aria-label="Aucune adresse e-mail" data-testid={`card-mail-${r.lead_id}`}><Mail size={14} /></button>}
        {r.phones?.[0] ? <CallLink phone={r.phones[0]} className="ibtn ibtn--sm" testid={`card-call-${r.lead_id}`}><Phone size={14} /></CallLink>
          : <button type="button" className="ibtn ibtn--sm" disabled title="Aucun numéro" aria-label="Aucun numéro" data-testid={`card-call-${r.lead_id}`}><Phone size={14} /></button>}
      </span>
    </footer>
  </article>
);

// Vue Liste en « Customer Directory » : grille de cartes contact
export const PipelineGrid = ({ rows, selected, toggle, onOpen, dormantDays }) => {
  const ref = useRef(null);
  const first = rows[0]?.lead_id;
  useEffect(() => {
    const cards = ref.current?.querySelectorAll(".cx-card");
    if (!cards?.length) return undefined;
    const tw = gsap.fromTo([...cards].slice(0, 24), { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: dur(0.7), ease: "power3.out", stagger: dur(0.03), clearProps: "transform,opacity" });
    hoverLift(ref.current);
    return () => tw.progress(1).kill();
  }, [first, rows.length]);
  if (!rows.length) return <div className="card pl-empty" data-testid="pipeline-grid-empty">Aucune opportunité ne correspond à ces filtres.</div>;
  return (
    <div className="cx-grid" ref={ref} data-testid="pipeline-grid">
      {rows.map((r) => <Card key={r.lead_id} r={r} checked={selected.includes(r.lead_id)} onToggle={toggle} onOpen={onOpen} dormantDays={dormantDays} />)}
    </div>
  );
};
